import { Button } from '@snsw/react-component-library';
import { ButtonHTMLAttributes, DetailedHTMLProps, ReactNode, useRef } from 'react';

type ButtonProps = DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>;

export interface NavigationButtonProps extends ButtonProps {
  label?: ReactNode;
  theme?: string;
  variant?: 'primary' | 'secondary' | 'tertiary' | 'link' | 'arrow' | 'back';

  /**
   * Sets the disabled state of the button.
   * @deprecated Please use `disabled` instead
   */
  isDisable?: boolean;

  /**
   * Handler to be invoked when the button is clicked.
   *
   * @deprecated Please use `onClick` instead
   */
  onClickHandler?: ButtonProps['onClick'];
}

const useShowDeprecatedPropertyWarningOnce = (
  id: NavigationButtonProps['id'],
  updatedPropertyName: string,
  deprecatedPropertyName: string,
  deprecatedPropertyValue: unknown
) => {
  const hasShownWarningRef = useRef(false);
  if (hasShownWarningRef.current || deprecatedPropertyValue == undefined) return;

  let warning = `The \`${deprecatedPropertyName}\` property is deprecated for Navigation Buttons; please use \`${updatedPropertyName}\` instead`;
  if (id) warning += `. Button id: ${id}`;
  console.warn(warning);

  hasShownWarningRef.current = true;
};

const useClickEventHandler = (
  id: NavigationButtonProps['id'],
  onClick: NavigationButtonProps['onClick'],
  onClickHandler: NavigationButtonProps['onClickHandler']
) => {
  useShowDeprecatedPropertyWarningOnce(id, 'onClick', 'onClickHandler', onClickHandler);
  return onClick ?? onClickHandler;
};

const useIsDisabled = (id: NavigationButtonProps['id'], disabled?: boolean, isDisable?: boolean) => {
  useShowDeprecatedPropertyWarningOnce(id, 'disabled', 'isDisable', isDisable);
  return disabled ?? isDisable;
};

export const NavigationButton = ({
  id,
  label,
  isDisable,
  disabled,
  onClick,
  onClickHandler,
  ...buttonProps
}: NavigationButtonProps) => {
  const handleClick = useClickEventHandler(id, onClick, onClickHandler);
  const isDisabled = useIsDisabled(id, disabled, isDisable);

  return (
    <Button id={id} disabled={isDisabled} onClick={handleClick} {...buttonProps}>
      {label}
    </Button>
  );
};

NavigationButton.defaultProps = {
  type: 'button',
};
